import { useCallback, useRef } from "react";
import { electronModule } from "@/modules/electron";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { RecallEventWindow, RecallMeetingDetectedEventKind } from "@/modules/electron/types";
import { EventContext } from "@/domains/metrics/context";
import { SystemPermissionsModal } from "@/store/system-permissions";
import { DateTime } from "luxon";
import { windowModule } from "@/modules/window";
import { Uuid } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import { createNoteEncodedContentForAutoStartVoice } from "@/store/note/helpers/auto-start-voice";
import { PreparedRecording } from "@/store/audio/recording/prepared-recording-controller";

export const useSystemAudioPermissionSynchronizer = (): void => {
  const { store } = useAppStore();

  const syncPermissions = useCallback(async () => {
    try {
      const [
        permissionMap,
        { enabled: teamsMeetingDetectionEnabled },
        { enabled: meetingNotificationEnabled },
      ] = await Promise.all([
        electronModule.recall.getPermissionStatus(),
        electronModule.getTeamsMeetingDetectionEnabled(),
        electronModule.getMeetingNotificationEnabled(),
      ]);

      logger.info({
        message: "[SystemAudioPermissionSynchronizer] Syncing",
        info: { teamsMeetingDetectionEnabled, permissionMap },
      });

      store.systemPermissions.setPermissionMap({ permissionMap });
      store.systemPermissions.setTeamsMeetingDetectionEnabled({
        enabled: teamsMeetingDetectionEnabled,
      });
      store.systemPermissions.setMeetingNotificationEnabled({
        enabled: meetingNotificationEnabled,
      });

      const { ui } = store.systemPermissions;

      if (!ui.activeModal) {
        return;
      }

      if (ui.activeModal === SystemPermissionsModal.Voice) {
        if (store.systemPermissions.hasRequiredVoicePermissions) {
          ui.closeModal();
        }
      }

      if (ui.activeModal === SystemPermissionsModal.Meeting) {
        if (store.systemPermissions.hasRequiredMeetingPermissions) {
          ui.closeModal();
        }
      }

      if (ui.activeModal === SystemPermissionsModal.AdvancedMeeting) {
        if (store.systemPermissions.hasRequiredAdvancedMeetingPermissions) {
          ui.closeModal();
        }
      }
    } catch (error) {
      logger.error({
        message: "[SystemAudioPermissionSynchronizer] Failed",
        info: { error: objectModule.safeErrorAsJson(error as Error) },
      });
    }
  }, [store.systemPermissions]);

  /**
   * Initialize permission state and subscribe to changes.
   */
  useEffectOnMount(() => {
    if (!store.interface.isDesktop) {
      return;
    }

    if (store.electron.isIntelMacDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       */
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const asyncLogic = async () => {
      await syncPermissions();

      try {
        unsubscribe = await electronModule.recall.onPermissionStatus?.({
          callback: () => {
            void syncPermissions();
          },
        });
      } catch (_) {
        /* empty */
      }
    };

    void asyncLogic();

    return () => {
      try {
        unsubscribe?.();
      } catch (_) {
        /* empty */
      }
    };
  });
};

/**
 * Listens to meeting detection events and handles them by either joining an ongoing recording
 * or creating a new note with the detected meeting window.
 */
export const useMeetingDetectionListener = (): void => {
  const { store } = useAppStore();
  const advancedMeetingPermissionShownRef = useRef(false);
  const preparedRecordingsRef = useRef(new Map<string, PreparedRecording>());

  const handleMeetingDetected = useCallback(
    async ({
      event,
      kind,
      debug,
    }: {
      event: {
        window: RecallEventWindow;
        info?: {
          calendarEventId?: string | null;
          audioRecordingId?: string;
          startTime?: string | null;
          endTime?: string | null;
          attendees?: string[] | null;
        };
      };
      kind: RecallMeetingDetectedEventKind;
      debug: boolean;
    }) => {
      const { window, info } = event;

      const hasFocus = document.hasFocus();
      logger.info({
        message: `[${kind}] Meeting event received (${window.id})`,
        info: {
          debug,
          window,
          info: info ?? null,
          kind,
          visibilityState: document.visibilityState,
          hidden: document.hidden,
          hasFocus,
        },
      });

      if (debug) {
        store.electron.pushRecallDebugEvent(window, kind);
        if (kind === RecallMeetingDetectedEventKind.MeetingDetected) {
          if (
            event.window.platform === "teams" &&
            !store.systemPermissions.hasRequiredAdvancedMeetingPermissions &&
            !advancedMeetingPermissionShownRef.current
          ) {
            store.systemPermissions.ui.openAdvancedMeetingPermissionsModal();
            electronModule.setAdvancedMeetingDetectionShown({ shown: true });
            advancedMeetingPermissionShownRef.current = true;
            return;
          }

          // Return early if it's not idle.
          if (store.audio.activeRecordingId || store.audio.recording.isInitializing) {
            return;
          }

          const preparedRecording = store.audio.getPreparedRecording();
          if (preparedRecording) {
            // Store prepared recording here to be restored below.
            preparedRecordingsRef.current.set(
              preparedRecording.audioRecordingId,
              preparedRecording
            );
            // Electron is going to use this to start the recording once the user clicks on the notification.
            const recallUploadCredentials = await preparedRecording.recallUploadCredentials;
            electronModule.recall.prepareRecording({
              audioRecordingId: preparedRecording.audioRecordingId,
              uploadToken: recallUploadCredentials.uploadToken,
              windowId: await preparedRecording.windowId,
            });
          }
        }
        return;
      }

      if (kind === RecallMeetingDetectedEventKind.MeetingClosed) {
        const activeAudioRecordingId = store.audio.activeRecordingId;

        const associatedNoteId = store.audio.activeRecordingNoteId;

        if (document.visibilityState === "hidden" || document.hidden) {
          logger.info({
            message:
              "[MeetingDetectionListener] Ignoring meeting closed event because page is hidden",
            info: {
              visibilityState: document.visibilityState,
              hidden: document.hidden,
              hasFocus,
              activeAudioRecordingId,
              associatedNoteId: associatedNoteId ?? null,
            },
          });
          return;
        }

        if (associatedNoteId) {
          store.navigation.goToNote({ noteId: associatedNoteId });
        }

        if (!activeAudioRecordingId) return;

        store.audio.finishRecording({
          audioRecordingId: activeAudioRecordingId,
        });
        return;
      }

      store.electron.pushRecallDebugEvent(window, "auto-start-voice");
      const preparedRecording = (() => {
        if (!info?.audioRecordingId) {
          return store.audio.recording.getPreparedRecording();
        }

        const preparedRecording = preparedRecordingsRef.current.get(info.audioRecordingId);
        if (preparedRecording) {
          preparedRecordingsRef.current.delete(info.audioRecordingId);
          preparedRecording.recallStarted = true;
          return preparedRecording;
        }

        return store.audio.recording.getPreparedRecording(info.audioRecordingId);
      })();

      const startRecording = (noteId: Uuid) => {
        logger.info({
          message: "[MeetingDetectionListener] Starting voice recording",
          info: {
            noteId,
            window,
            info: info ?? null,
            audioRecordingId: preparedRecording.audioRecordingId,
          },
        });

        const tooSoonToJoinMeeting =
          // Detected meeting.
          !!info?.startTime &&
          // Calendar meeting is close so we join for the user.
          DateTime.fromISO(info.startTime).diffNow().as("minutes") > 1;
        if (tooSoonToJoinMeeting) return;

        if (window?.url) {
          void windowModule.openInNewTab({ url: new URL(window.url) });
        }
        void store.audio.createAndStartAudioRecording({
          preparedRecording,
          expandVoicePopover: false,
          associatedNoteId: noteId,
          associatedMeetingWindowId: window.id,
        });
      };

      const onCreateError = () => {
        store.audio.trashRecording({ audioRecordingId: preparedRecording.audioRecordingId });
      };

      const calendarEvent = info?.calendarEventId
        ? await store.spaceAccountCalendarEvents.getAsync(info?.calendarEventId)
        : undefined;

      const associatedNoteId = await (async () => {
        if (!calendarEvent) return;

        return calendarEvent?.associatedNote?.id;
      })();

      if (associatedNoteId) {
        startRecording(associatedNoteId);
        store.navigation.goToNote({ noteId: associatedNoteId });
        return;
      }

      // Create a new note with the detected meeting window as autoStartVoice
      const noteId = uuidModule.generate();
      store.notes.composeNewNote({
        eventContext: EventContext.MeetingDetectionNotification,
        calendarEvent,
        noteContent: createNoteEncodedContentForAutoStartVoice({
          windowId: window.id,
          windowTitle: window.title,
          windowUrl: window.url,
          platform: window.platform,
          startTime: info?.startTime ?? undefined,
          endTime: info?.endTime ?? undefined,
          attendees: info?.attendees ?? [],
        }),
        noteId,
        autoFocusPosition: "end",
        onBeforeCreate: () => startRecording(noteId),
        onCreateError,
      });
    },
    [store]
  );

  useEffectOnMount(() => {
    if (!store.interface.isDesktop) {
      return;
    }

    if (store.electron.isIntelMacDesktop) {
      /**
       * This feature isn't currently supported on Intel-based MacOS devices.
       */
      return;
    }

    let unsubscribe: (() => void) | undefined;

    const asyncLogic = async () => {
      electronModule.setIsClientReady({ isReady: true });

      try {
        const { shown: advancedMeetingDetectionShown } =
          await electronModule.getAdvancedMeetingDetectionShown();
        advancedMeetingPermissionShownRef.current = advancedMeetingDetectionShown;
      } catch (error) {
        logger.error({
          message: "Failed to get advanced meeting detection shown",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }

      try {
        unsubscribe = await electronModule.recall.onMeetingEvent?.({
          callback: handleMeetingDetected,
        });
      } catch (error) {
        logger.error({
          message: "Failed to subscribe to meeting detection events",
          info: { error: objectModule.safeErrorAsJson(error as Error) },
        });
      }
    };

    void asyncLogic();

    return () => {
      electronModule.setIsClientReady({ isReady: false });
      try {
        unsubscribe?.();
      } catch (_) {
        /* empty */
      }
    };
  });
};
